/**
 * Workspace-scoped dashboard page RBAC — single instance for the whole protected app.
 * Avoids re-fetch / "show all nav items" flash when AppLayout remounts on route change.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { getMePageAccess } from "@/api/rbacSettingsApi";

/** @type {import("react").Context<{
 *   allowedPageKeys: string[],
 *   loading: boolean,
 *   canAccessPage: (pageKey: string) => boolean,
 *   refresh: () => Promise<void>,
 * } | null>} */
const DashboardPageAccessContext = createContext(null);

export function DashboardPageAccessProvider({ children }) {
  const { activeWorkspaceId } = useWorkspace();
  const [allowedPageKeys, setAllowedPageKeys] = useState(/** @type {string[]} */ ([]));
  const [loading, setLoading] = useState(true);

  const fetchAccess = useCallback(async () => {
    if (!activeWorkspaceId) {
      setAllowedPageKeys([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await getMePageAccess();
      const keys = Array.isArray(data?.allowedPageKeys) ? data.allowedPageKeys : [];
      setAllowedPageKeys(keys);
    } catch {
      setAllowedPageKeys([]);
    } finally {
      setLoading(false);
    }
  }, [activeWorkspaceId]);

  useEffect(() => {
    setAllowedPageKeys([]);
    setLoading(true);
    void fetchAccess();
  }, [fetchAccess]);

  const canAccessPage = useCallback(
    (pageKey) => {
      if (!pageKey) return true;
      if (!activeWorkspaceId) return false;
      if (allowedPageKeys.length > 0) {
        return allowedPageKeys.includes(pageKey);
      }
      return false;
    },
    [allowedPageKeys, activeWorkspaceId]
  );

  const value = useMemo(
    () => ({
      allowedPageKeys,
      loading,
      canAccessPage,
      refresh: fetchAccess,
    }),
    [allowedPageKeys, loading, canAccessPage, fetchAccess]
  );

  return (
    <DashboardPageAccessContext.Provider value={value}>{children}</DashboardPageAccessContext.Provider>
  );
}

export function useDashboardPageAccess() {
  const ctx = useContext(DashboardPageAccessContext);
  if (!ctx) {
    throw new Error("useDashboardPageAccess must be used within DashboardPageAccessProvider");
  }
  return ctx;
}
